import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { usePathname, useRouter } from 'expo-router'
import { useCallback, useState } from 'react'
import { type Control, type FieldPath, useController } from 'react-hook-form'
import { FlatList, StyleSheet, TouchableOpacity, View } from 'react-native'
import {
    ActivityIndicator,
    Dialog,
    HelperText,
    Icon,
    Searchbar,
    Text,
    useTheme,
} from 'react-native-paper'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useConsumeNewlyCreated } from '@/lib/newlyCreatedRecord'
import { useStore } from '@/lib/pocketbase'
import type { JobFormData } from '@/types/jobs'
import type { Addresses } from '@/types/pbSchema'
import { Button } from '@/ui/Button'
import { PickerDialog } from '@/ui/PickerDialog'

export interface CustomerWithAddress {
    id: string
    name: string
    identifier: string
    address?: Addresses
}

interface CustomerSelectProps {
    control: Control<JobFormData>
    name: FieldPath<JobFormData>
    label: string
    required?: boolean
    errorMessage?: string
    onCustomerChange?: (customer: CustomerWithAddress) => void
}

interface CustomerListItem {
    id: string
    name: string
    customer: CustomerWithAddress
}

interface CustomerDialogProps {
    visible: boolean
    onDismiss: () => void
    customerList: CustomerListItem[]
    onSelectCustomer: (item: CustomerListItem) => void
    onAddNew: () => void
}

function CustomerDialog({
    visible,
    onDismiss,
    customerList,
    onSelectCustomer,
    onAddNew,
}: CustomerDialogProps) {
    const theme = useTheme()
    const [searchQuery, setSearchQuery] = useState('')

    const filteredCustomerList = searchQuery
        ? customerList.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
        : customerList

    const showSearchBox = customerList.length > 5

    const handleDismiss = useCallback(() => {
        setSearchQuery('')
        onDismiss()
    }, [onDismiss])

    const handleSelect = (item: CustomerListItem) => {
        setSearchQuery('')
        onSelectCustomer(item)
    }

    const handleAddNew = () => {
        setSearchQuery('')
        onAddNew()
    }

    return (
        <PickerDialog visible={visible} onDismiss={handleDismiss}>
            <Dialog.Title style={{ color: theme.colors.onSurface }}>Select Customer</Dialog.Title>
            <Dialog.Content>
                {showSearchBox && (
                    <Searchbar
                        placeholder="Search"
                        value={searchQuery}
                        onChangeText={setSearchQuery}
                        style={[styles.searchBar, { backgroundColor: theme.colors.surfaceVariant }]}
                    />
                )}
                <FlatList
                    data={filteredCustomerList}
                    keyExtractor={item => item.id}
                    style={styles.list}
                    renderItem={({ item }) => (
                        <TouchableOpacity style={styles.item} onPress={() => handleSelect(item)}>
                            <Text
                                variant="bodyLarge"
                                style={[styles.itemText, { color: theme.colors.onSurface }]}
                            >
                                {item.name}
                            </Text>
                        </TouchableOpacity>
                    )}
                    ListEmptyComponent={
                        <Text
                            variant="bodyMedium"
                            style={{
                                color: theme.colors.onSurfaceVariant,
                                textAlign: 'center',
                                paddingVertical: 16,
                            }}
                        >
                            {searchQuery ? 'No customers found' : 'No customers yet'}
                        </Text>
                    }
                />
            </Dialog.Content>
            <Dialog.Actions style={styles.actions}>
                <Button title="Close" onPress={handleDismiss} />
                <Button title="Add New" mode="contained" icon="plus" onPress={handleAddNew} />
            </Dialog.Actions>
        </PickerDialog>
    )
}

function getSelectedCustomerValue(
    value: unknown
): { id: string; name: string; identifier?: string } | undefined {
    if (value && typeof value === 'object' && 'id' in value && 'name' in value) {
        const candidate = value as {
            id?: unknown
            name?: unknown
            identifier?: unknown
        }
        if (typeof candidate.id === 'string' && typeof candidate.name === 'string') {
            return {
                id: candidate.id,
                name: candidate.name,
                identifier: candidate.identifier as string | undefined,
            }
        }
    }
    return undefined
}

export function CustomerSelect({
    control,
    name,
    label,
    required,
    errorMessage,
    onCustomerChange,
}: CustomerSelectProps) {
    const { org: currentOrg } = useCurrentOrg()
    const [customersCollection] = useStore('customers')
    const theme = useTheme()
    const router = useRouter()
    const pathname = usePathname()
    const [dialogVisible, setDialogVisible] = useState(false)

    const { isLoading, data: customers = [] } = useLiveQuery(
        q =>
            q
                .from({ customers: customersCollection })
                .where(({ customers }) => eq(customers.org, currentOrg.id))
                .orderBy(({ customers }) => customers.name, 'asc'),
        [currentOrg.id]
    )

    const customerList: CustomerListItem[] = customers.map(customer => ({
        id: customer.id,
        name: customer.name,
        customer: {
            id: customer.id,
            name: customer.name,
            identifier: customer.identifier,
            address: customer.expand?.address,
        },
    }))

    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
        rules: required
            ? {
                  required: `${label} is required`,
                  validate: value => {
                      if (
                          value &&
                          typeof value === 'object' &&
                          'id' in value &&
                          typeof value.id === 'string' &&
                          value.id.trim().length > 0
                      ) {
                          return true
                      }
                      return `${label} is required`
                  },
              }
            : undefined,
    })

    const selectedCustomer = getSelectedCustomerValue(field.value)
    const displayValue = selectedCustomer?.name ?? ''
    const effectiveError = errorMessage || error?.message

    const handleSelectCustomer = (item: CustomerListItem) => {
        field.onChange(item.customer)
        onCustomerChange?.(item.customer)
        setDialogVisible(false)
    }

    const handleAddNew = () => {
        setDialogVisible(false)
        const returnTo = encodeURIComponent(pathname)
        router.push(`/${currentOrg.slug}/settings/customers/new?returnTo=${returnTo}`)
    }

    useConsumeNewlyCreated('customers', id => {
        const created = customerList.find(c => c.id === id)
        if (created) {
            field.onChange(created.customer)
            onCustomerChange?.(created.customer)
        }
    })

    return (
        <View style={styles.container}>
            {isLoading ? (
                <View style={styles.loadingContainer}>
                    <Text variant="bodyMedium">
                        {label}
                        {required && ' *'}
                    </Text>
                    <ActivityIndicator size="small" />
                </View>
            ) : (
                <>
                    <Text
                        variant="labelMedium"
                        style={[styles.label, { color: theme.colors.onSurfaceVariant }]}
                    >
                        {label}
                        {required && ' *'}
                    </Text>
                    <TouchableOpacity
                        testID="customer-select"
                        onPress={() => setDialogVisible(true)}
                        style={[
                            styles.picker,
                            {
                                backgroundColor: theme.colors.surface,
                                borderColor: effectiveError
                                    ? theme.colors.error
                                    : theme.colors.outline,
                            },
                        ]}
                    >
                        <Text
                            variant="bodyLarge"
                            style={
                                displayValue
                                    ? { color: theme.colors.onSurface }
                                    : { color: theme.colors.onSurfaceVariant }
                            }
                        >
                            {displayValue || 'Select a customer'}
                        </Text>
                        <Icon
                            source="chevron-down"
                            size={24}
                            color={theme.colors.onSurfaceVariant}
                        />
                    </TouchableOpacity>
                    {effectiveError ? (
                        <HelperText type="error" visible>
                            {effectiveError}
                        </HelperText>
                    ) : null}

                    <CustomerDialog
                        visible={dialogVisible}
                        onDismiss={() => setDialogVisible(false)}
                        customerList={customerList}
                        onSelectCustomer={handleSelectCustomer}
                        onAddNew={handleAddNew}
                    />
                </>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        marginBottom: 16,
        paddingTop: 16,
    },
    loadingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    label: {
        marginBottom: 4,
    },
    picker: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderWidth: 1,
        borderRadius: 4,
        padding: 12,
        minHeight: 56,
    },
    searchBar: {
        marginBottom: 12,
    },
    list: {
        maxHeight: 300,
    },
    item: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 4,
    },
    itemText: {
        flex: 1,
    },
    actions: {
        justifyContent: 'space-between',
        paddingHorizontal: 16,
    },
})
